<template>
  <Popover v-model:open="open">
    <PopoverAnchor as-child>
      <InputGroup
        v-bind="rootAttrs"
        :class="rootClass"
      >
        <div
          data-slot="input-group-control"
          role="combobox"
          :aria-expanded="open"
          tabindex="0"
          :title="selectionSummary"
          class="flex min-h-9 flex-1 flex-wrap items-center gap-1 px-3 py-2 text-sm text-foreground/80 outline-none focus-visible:outline-none"
          @click="openDropdown"
          @keydown.enter.prevent="openDropdown"
          @keydown.space.prevent="openDropdown"
          @keydown.arrow-down.prevent="openDropdown"
        >
          <template v-if="hasSelection">
            <template v-if="showTextSelection">
              <span class="text-foreground/80">
                {{ singleSelectedLabel }}
              </span>
            </template>
            <template v-else>
              <Badge
                v-for="item in visibleSelectedItems"
                :key="item.value"
                variant="secondary"
                class="cursor-pointer"
                @click.stop="toggleOption(item.value)"
              >
                {{ item.label }}
                <X class="ml-1 h-3 w-3" />
              </Badge>
              <span
                v-if="hiddenItemCount > 0"
                class="text-xs text-muted-foreground"
              >
                {{ moreLabel }}
              </span>
            </template>
          </template>
          <span v-else class="text-muted-foreground">
            {{ placeholder }}
          </span>
        </div>
        <PopoverTrigger as-child>
          <InputGroupButton
            :aria-label="toggleAriaLabel"
            :aria-expanded="open"
            class="ml-auto shrink-0 text-muted-foreground"
          >
            <ChevronsUpDown class="h-4 w-4" />
          </InputGroupButton>
        </PopoverTrigger>
      </InputGroup>
    </PopoverAnchor>
    <PopoverContent
      class="w-[var(--reka-popover-trigger-width)] p-0"
      align="start"
      @keydown.enter="onCommandEnter"
    >
      <Command
        v-model:search-term="searchTerm"
        :reset-search-term-on-select="false"
        v-model:selected-value="activeValue"
      >
        <CommandInput :placeholder="searchInputPlaceholder" />
        <CommandEmpty>{{ emptyText }}</CommandEmpty>
        <CommandList>
          <CommandGroup>
            <CommandItem
              v-for="option in filteredOptions"
              :key="option.value"
              :value="option.value"
              @select="handleSelect(option.value)"
            >
              <div class="flex items-center">
                <Check
                  :class="cn(
                    'mr-2 h-4 w-4',
                    modelValue.includes(option.value) ? 'opacity-100' : 'opacity-0'
                  )"
                />
                {{ option.label }}
              </div>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
      <div v-if="showSelectedList" class="border-t p-2">
        <div class="flex flex-wrap gap-1">
          <Badge 
            v-for="value in selectedValues" 
            :key="value"
            variant="secondary"
            class="cursor-pointer"
            @click="toggleOption(value)"
          >
            {{ options.find(opt => opt.value === value)?.label || value }}
            <X class="ml-1 h-3 w-3" />
          </Badge>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useAttrs, watch } from 'vue'
import { Check, ChevronsUpDown, X } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { InputGroup, InputGroupButton } from '@/components/ui/input-group'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface Option {
  label: string
  value: string
}

interface Props {
  options: Option[]
  placeholder?: string
  modelValue?: string[]
  multiple?: boolean
  filterable?: boolean
  allowCreate?: boolean
  singleSelectionMode?: 'badge' | 'text'
  // Single-select only: seed the search box with the current value on open.
  // Turn off when the point of opening is to browse all options — the seeded
  // term filters the list down to (usually) nothing.
  prefillSearchOnOpen?: boolean
  searchPlaceholder?: string
  emptyText?: string
  toggleAriaLabel?: string
  moreText?: string
}

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select options...',
  modelValue: () => [],
  multiple: true,
  filterable: true,
  allowCreate: false,
  singleSelectionMode: 'badge',
  prefillSearchOnOpen: true,
  emptyText: 'No results found.',
  toggleAriaLabel: 'Toggle options',
  moreText: '+{n} more',
})

const emit = defineEmits<{
  'update:modelValue': [value: string[]]
}>()

const open = ref(false)
const searchTerm = ref('')
const attrs = useAttrs() as Record<string, unknown> & { class?: unknown }

const selectedValues = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const activeValue = ref<string | undefined>()
const filteredOptions = computed(() => {
  const term = searchTerm.value.trim()
  let opts = [...props.options]
  
  if (props.allowCreate && term && !opts.some(opt => opt.value === term)) {
    opts.unshift({
      label: term,
      value: term
    })
  }
  
  if (term && props.filterable) {
    return opts.filter(option => 
      option.label.toLowerCase().includes(term.toLowerCase())
    )
  }
  
  return opts
})

const rootAttrs = computed<Record<string, unknown>>(() => {
  const rest = { ...attrs } as Record<string, unknown>
  delete rest.class
  return rest
})

const rootClass = computed(() => cn('w-full cursor-pointer', attrs.class as string))

const maxVisibleBadges = 3

const selectedDisplayItems = computed(() =>
  selectedValues.value.map(value => {
    const optionLabel = props.options.find(opt => opt.value === value)?.label ?? value
    return { value, label: optionLabel }
  }),
)

const visibleSelectedItems = computed(() =>
  selectedDisplayItems.value.slice(0, maxVisibleBadges),
)

const hiddenItemCount = computed(
  () => Math.max(selectedDisplayItems.value.length - visibleSelectedItems.value.length, 0),
)

const hasSelection = computed(() => selectedDisplayItems.value.length > 0)

const selectionSummary = computed(() =>
  hasSelection.value
    ? selectedDisplayItems.value.map(item => item.label).join(', ')
    : props.placeholder,
)

const showTextSelection = computed(
  () => !props.multiple && props.singleSelectionMode === 'text',
)

const singleSelectedLabel = computed(
  () => selectedDisplayItems.value[0]?.label ?? '',
)

const showSelectedList = computed(
  () => props.multiple && selectedValues.value.length > 0,
)

function openDropdown() {
  open.value = true
}

watch(open, async (isOpen) => {
  if (!isOpen || props.multiple) {
    return
  }
  await nextTick()
  searchTerm.value = props.prefillSearchOnOpen ? (singleSelectedLabel.value || '') : ''
}, { flush: 'post' })

const searchInputPlaceholder = computed(() => {
  if (props.searchPlaceholder) return props.searchPlaceholder
  return `Search ${props.placeholder.toLowerCase()}...`
})

const moreLabel = computed(() => {
  const template = String(props.moreText || '+{n} more')
  return template.replace('{n}', String(hiddenItemCount.value))
})

function applySelection(value: string) {
  const currentValues = [...selectedValues.value]
  if (props.multiple) {
    if (!currentValues.includes(value)) {
      currentValues.push(value)
    }
  } else {
    currentValues.splice(0, currentValues.length, value)
  }
  selectedValues.value = currentValues
}

function toggleOption(value: string) {
  const currentValues = [...selectedValues.value]
  const index = currentValues.indexOf(value)
  
  if (index > -1) {
    currentValues.splice(index, 1)
  } else {
    if (props.multiple) currentValues.push(value)
    else currentValues.splice(0, currentValues.length, value)
  }
  
  selectedValues.value = currentValues
}

function onCommandEnter(event: KeyboardEvent) {
  const term = searchTerm.value.trim()
  if (!term) {
    return
  }
  const match = props.options.find(opt => opt.value === term)
  if (!match && !props.allowCreate) {
    return
  }
  event.preventDefault()
  const nextValue = match ? match.value : term
  activeValue.value = nextValue
  applySelection(nextValue)
  searchTerm.value = ""
  if (!props.multiple) {
    open.value = false
  }
}

function handleSelect(value: string, event?: Event) {
  const term = searchTerm.value
  event?.preventDefault?.()
  activeValue.value = value
  toggleOption(value)
  if (!props.multiple) open.value = false
  setTimeout(() => {
    searchTerm.value = term
    activeValue.value = value
  }, 0)
}
</script>
